import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Star, ExternalLink } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["removed", "Removed"], ["", "All"]];

function ProfileRow({ p, onPatch, busy }) {
  const [notes, setNotes] = useState(p.moderation_notes || "");
  return (
    <div className="glass-panel p-4 grid lg:grid-cols-[1fr_auto] gap-4" data-testid={`admin-creator-${p.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bebas uppercase tracking-wider text-white text-lg">{p.display_name}</span>
          <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-white/15 text-white/60" data-testid="admin-creator-status">{p.status}</span>
          {p.featured && <span className="text-[10px] text-amber-300 inline-flex items-center gap-1"><Star className="h-3 w-3" /> featured</span>}
          <span className="text-[10px] text-white/40">{p.badge}{p.affiliate_linked ? " · affiliate verified" : ""}</span>
          {!p.consent_public && <span className="text-[10px] text-rose-300">no public consent</span>}
        </div>
        <div className="text-[11px] text-white/50 mt-1">{p.owner_email} · {p.category} · {new Date(p.created_at).toLocaleDateString()}</div>
        {p.bio && <p className="text-sm text-white/75 mt-2">{p.bio}</p>}
        <div className="flex flex-wrap gap-1 mt-2">
          {(p.social_links || []).map((s) => (
            <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-[11px] px-2 py-0.5 border border-white/15 rounded text-lifted-green inline-flex items-center gap-1">
              {s.platform} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Private moderation notes" className="input-lifted flex-1 text-xs" data-testid="admin-creator-notes" />
          <button disabled={busy} onClick={() => onPatch(p.id, { moderation_notes: notes })} className="btn-ghost text-xs" data-testid="admin-creator-notes-save">Save</button>
        </div>
      </div>
      <div className="flex lg:flex-col gap-2 flex-wrap">
        <button disabled={busy || p.status === "approved" || !p.consent_public} onClick={() => onPatch(p.id, { status: "approved" })} className="btn-lifted text-xs disabled:opacity-40" data-testid="admin-creator-approve">Approve</button>
        <button disabled={busy || p.status === "rejected"} onClick={() => onPatch(p.id, { status: "rejected" })} className="btn-ghost text-xs disabled:opacity-40" data-testid="admin-creator-reject">Reject</button>
        <button disabled={busy || p.status !== "approved"} onClick={() => onPatch(p.id, { featured: !p.featured })} className="btn-ghost text-xs disabled:opacity-40" data-testid="admin-creator-feature">{p.featured ? "Unfeature" : "Feature"}</button>
        <button disabled={busy || p.status === "removed"} onClick={() => onPatch(p.id, { status: "removed" })} className="btn-ghost text-xs text-rose-300 disabled:opacity-40" data-testid="admin-creator-remove">Remove</button>
      </div>
    </div>
  );
}

export function AdminCreatorModeration() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/admin/creators${status ? `?status=${status}` : ""}`);
      setRows(data.profiles || []);
    } catch (e) { setErr(apiErrorMessage(e, "Could not load profiles")); setRows([]); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  async function onPatch(id, body) {
    setBusy(id); setErr("");
    try { await api.patch(`/admin/creators/${id}`, body); await load(); }
    catch (e) { setErr(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(""); }
  }

  return (
    <div data-testid="admin-creator-moderation">
      <div className="flex gap-1 flex-wrap mb-4">
        {FILTERS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatus(v)} data-testid={`creator-filter-${v || "all"}`}
            className={`text-[10px] font-bebas uppercase tracking-[0.2em] px-3 py-1.5 rounded border ${status === v ? "bg-lifted-green text-black border-lifted-green" : "border-white/15 text-white/70"}`}>{l}</button>
        ))}
      </div>
      {err && <div className="glass-panel p-3 mb-3 border border-rose-500/40 text-rose-200 text-sm" data-testid="creator-moderation-error">{err}</div>}
      {rows === null ? <Loader2 className="h-4 w-4 animate-spin text-white/50" /> : rows.length === 0 ? (
        <div className="glass-panel p-8 text-center text-white/50 text-sm" data-testid="creator-moderation-empty">No profiles in this state.</div>
      ) : (
        <div className="space-y-3">{rows.map((p) => <ProfileRow key={p.id + p.updated_at} p={p} onPatch={onPatch} busy={busy === p.id} />)}</div>
      )}
    </div>
  );
}
